import { createContext, FC, ReactNode, useContext } from "react";
import { useAtom } from "jotai";
import { authAtom } from "./authAtom";
import { Toast } from "../components/atom/Toast/Toast";
import api from "../utils/api";
import { LOGIN_URL } from "../utils/endpoints";
import jwtDecode from "jwt-decode";
import { AuthContextType } from "../types/User/AuthContextType";
import { AuthUser } from "../types/User/AuthUser";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [auth, setAuth] = useAtom(authAtom);
  const { addToast } = Toast();

  const login = async (data: {
    email: string;
    password: string;
  }): Promise<AuthUser | null> => {
    try {
      const response = await api.post(LOGIN_URL, {
        email: data.email,
        password: data.password,
      });
      const {
        token,
        forgeryToken,
        provinceId,
        provinceName,
        countyId,
        countyName,
        roleName,
        roleLevel,
        partner,
      } = response.data;
      if (token) {
        const userDetails = {
          roleName,
          roleLevel,
          provinceId,
          provinceName,
          countyId,
          countyName,
          partner,
        };
        sessionStorage.setItem("@sisanForgeryToken", forgeryToken);
        sessionStorage.setItem("@sisantoken", token);
        localStorage.setItem("@sisanUserDetails", JSON.stringify(userDetails));
        const decodedToken: any = jwtDecode(token);

        const user: AuthUser = {
          id: decodedToken.nameid,
          email: decodedToken.email,
          name: decodedToken.unique_name,
          permissions: decodedToken.role,
          province: {
            id: provinceId,
            name: provinceName,
          },
          county: {
            id: countyId,
            name: countyName,
          },
          role: {
            name: roleName,
            level: {
              id: roleLevel,
            },
          },
          partner: partner,
        };

        setAuth({
          isAuthenticated: true,
          user,
          token,
          forgeryToken,
        });
        return user;
      }

      return null;
    } catch (error: any) {
      const errors = error.response?.data?.errors || [
        "O email ou senha estão incorrectos",
      ];
      errors.forEach((err: string) =>
        addToast({ title: err, status: "error" }),
      );
      return null;
    }
  };

  const logout = () => {
    sessionStorage.removeItem("@sisanForgeryToken");
    sessionStorage.removeItem("@sisantoken");
    localStorage.removeItem("@sisanUserDetails");
    setAuth({
      isAuthenticated: false,
      user: null,
      token: null,
      forgeryToken: null,
    });
  };

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, AuthContext, useAuth };
